import LinkIcon from '@mui/icons-material/Link'
import {Box, Chip, MenuItem, Stack, Tooltip, Typography} from '@mui/material'
import type {MouseEvent, ReactElement, ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import BasicLabel from '@shared/components/base/BasicLabel.tsx'
import BasicPopoverWithSearch from '@shared/components/base/BasicPopoverWithSearch.tsx'
import {useCustomTranslation} from '@hooks/useCustomTranslation.ts'

export type PickerOption = {
  id: string
  name: string
  // Rendered under the name in the dropdown row (e.g. a program's description).
  description?: string | null
}

/**
 * The half of the props that does not participate in the single/multi
 * discriminant. Exported because `Partial<PickerProps>` collapses the union —
 * TypeScript can no longer prove `multiple` is `true` for the multi branch — so
 * anything overriding props generically (a wrapper, a test helper) wants this.
 */
export type PickerCommonProps = {
  // Name of the dimension being filtered ("Status", "Equipment"). Drives the
  // trigger tooltip and the accessible label.
  label: string
  options: PickerOption[]
  // Server-side search. When omitted the component filters `options` itself on
  // the typed term, which is what every "the whole option list is already in
  // memory" filter needs — no caller should hand-roll `.includes()` again.
  onSearch?: (search: string) => void
  isLoading?: boolean
  // Name of the parent selection this filter is scoped by (e.g. the line an
  // equipment filter is narrowed to). Shows the "filter for X" header and the
  // link badge on the trigger.
  parentName?: string
  icon?: ReactElement
  // Show `label` above the trigger, like BasicSelect and BasicInput do -- for a
  // form field next to labelled inputs. Off for filters, where the tooltip and
  // placeholder name the dimension.
  displayLabel?: boolean
  // Dim text shown in the trigger while nothing is selected (e.g. "All Roles").
  // Without it the trigger shows only the icon and chevron.
  placeholder?: string
  // Label of the clear row. Defaults to "None", which reads correctly for a
  // filter whose empty state means "no value" — but not for one whose empty
  // state means "every value": on a people filter "Keine" reads as "unassigned"
  // rather than "everyone". Pass the wording the dimension needs.
  clearLabel?: string
  // Lock the filter (e.g. gated on a prerequisite selection) — the trigger
  // button is disabled and `tooltip` explains why.
  disabled?: boolean
  // Trigger tooltip. Defaults to the label; callers that are filtering pass the
  // filtering wording (FilterBar does this for the pickers it lays out).
  tooltip?: string
  // Trigger height. The default 'large' pins the button to 43px, which is right
  // on its own but sits proud of a compact filter row. Anything else makes the
  // button fill its container, so the row — not the component — decides the
  // height and every control shares one baseline.
  size?: 'small' | 'medium' | 'large'
  minWidth?: string
  maxWidth?: string
  // Cap the dropdown popover width. Defaults to the shared 400px; pass '100%'
  // (e.g. in a side panel) to let long option names show unbounded.
  popoverMaxWidth?: string
  hasMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
  // The options request failed. Shown in the list with a retry row, so a failed
  // load does not read as an empty list.
  isError?: boolean
  onRetry?: () => void
  // Told when the dropdown opens and closes, so a caller can hold its options
  // query until someone actually opens the list.
  onOpenChange?: (open: boolean) => void
  // Some dimensions have no "unset" state — a status filter whose own option
  // list already includes "All" would otherwise show a redundant clear row.
  clearable?: boolean
  // Options that are visible but not selectable, with a shared explanation.
  disabledIds?: string[]
  disabledTooltip?: string
  // Custom rendering for an option, used for the dropdown rows and for the
  // trigger — in single mode for the selected option, in multi mode for each
  // selected entry in place of the default chip (e.g. the incident status and
  // task priority filters, which draw the same chip the column draws).
  renderOption?: (option: PickerOption) => ReactNode
  // Drop the search row in the dropdown. For a list that is both complete and
  // short -- three statuses, two modes -- the box is most of the popover and
  // there is nothing to search. Opt-in, so every other picker keeps it.
  hideSearch?: boolean
  // Rendered above the search box, for a control that scopes its own list (the
  // rollup signal picker's equipment/all toggle). Not a row: it stays put while
  // the list below it searches and grows.
  headerContent?: ReactNode
  // Server-searched lists that start empty (the member searches answer only a
  // typed term): shown in place of "no data" while nothing is typed, so an
  // empty list reads as "search" rather than as "nobody there".
  searchPrompt?: string
  // Multi-select only: a summary to show in the trigger in place of one chip
  // per selection (e.g. "State lanes (7)"), for rows too dense for chips.
  selectedLabel?: ReactNode
  // Multi-select only: rows announce themselves as checkboxes
  // (role="menuitemcheckbox" + aria-checked). Opt-in, because many callers'
  // tests and specs still address the rows as plain menu items.
  checkableOptions?: boolean
  // Id of the text that explains this field (e.g. its helper text), linked to
  // the trigger with aria-describedby.
  ariaDescribedBy?: string
  dataCy?: string
  // Unique data-testid for the root. Defaults to the generic 'mbai-picker'.
  dataTestId?: string
}

type SingleProps = PickerCommonProps & {
  multiple?: false
  // '' and null both mean "nothing selected".
  value: string | null
  onChange: (id: string) => void
}

type MultiProps = PickerCommonProps & {
  multiple: true
  value: string[]
  onChange: (ids: string[]) => void
}

export type PickerProps = SingleProps | MultiProps

const CLEAR_ID = ''

/**
 * The selection control for the whole app: a searchable dropdown that works in
 * single- or multi-select mode.
 *
 * Deliberately not called a filter. It has no predicate and no dataset — it
 * narrows only its own option list, which every select does. Filtering is one
 * of the things you do with it: `FilterBar` composes Pickers into a filter row
 * and supplies the filtering vocabulary, and `useFilterParams` binds the result
 * to the URL. A scope picker or a form field is an equally valid consumer.
 *
 * Presentational by design — it never touches the URL, so it can equally serve
 * draft state in a modal or a widget config.
 */
const Picker = (props: PickerProps): ReactElement => {
  const {
    label,
    options,
    onSearch,
    isLoading = false,
    parentName = '',
    icon,
    placeholder,
    clearLabel,
    displayLabel = false,
    disabled = false,
    tooltip,
    size = 'large',
    minWidth = '200px',
    maxWidth,
    popoverMaxWidth,
    hasMore = false,
    onLoadMore,
    isLoadingMore = false,
    isError = false,
    onRetry,
    onOpenChange,
    clearable = true,
    disabledIds = [],
    disabledTooltip,
    renderOption,
    selectedLabel,
    hideSearch = false,
    headerContent,
    searchPrompt,
    checkableOptions = false,
    ariaDescribedBy,
    dataCy,
    dataTestId = 'mbai-picker',
  } = props
  const [t] = useCustomTranslation('common')
  const [searchTerm, setSearchTerm] = useState('')

  // Keep the latest onSearch in a ref so the handler below has a stable
  // identity. Callers (e.g. useHierarchyFilters) recreate onSearch on every
  // render; an unstable handler reaching the debounce inside SimpleSearch is
  // what broke Digital Twin search — the typed term never fired a fetch. See
  // ADO Bug #5387 / Intercom "Suche im Zwilling defekt".
  const onSearchRef = useRef(onSearch)
  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // Whether the dropdown is open, and the last term handed to onSearch. Refs,
  // not state, because both are read in the same tick they change: the close
  // must see a term forwarded a moment ago even if React has not re-rendered
  // since, and a search arriving after the close must be recognised as late.
  const isOpenRef = useRef(false)
  const sentTermRef = useRef('')

  const onOpenChangeRef = useRef(onOpenChange)
  useEffect(() => {
    onOpenChangeRef.current = onOpenChange
  }, [onOpenChange])

  const handleOpen = useCallback((): void => {
    isOpenRef.current = true
    onOpenChangeRef.current?.(true)
  }, [])

  // SimpleSearch (inside BasicPopoverWithSearch) already debounces by 300ms and
  // already guards the unstable-callback case, so this forwards straight
  // through. The old BasicFilter debounced a second time on top of it, which
  // only ever added 300ms of latency before the request went out.
  //
  // Except once the dropdown has closed: the popover, and the search box in it,
  // stays mounted through its exit transition, so a term typed just before the
  // close still comes out of the debounce afterwards. Forwarding it would narrow
  // the options again behind a closed dropdown (AB#6388).
  const handleSearchChange = useCallback((term: string): void => {
    if (!isOpenRef.current) {
      return
    }
    sentTermRef.current = term
    setSearchTerm(term)
    onSearchRef.current?.(term)
  }, [])

  // The search box lives inside the popover, so closing the dropdown unmounts it
  // and it comes back empty — but the term it produced survives, here and in
  // whatever the caller did with it (usually a query arg). Reopening then showed
  // an empty box over a still-narrowed list, with no way to widen it again
  // except typing and deleting a character. Drop the term on close so the box
  // and the options agree.
  //
  // Read from the ref, not from `searchTerm`: the debounced search can go out
  // and the dropdown close before the next render, and a closure over the state
  // then still saw '' and told the server nothing (AB#6388).
  const handleClose = useCallback((): void => {
    isOpenRef.current = false
    onOpenChangeRef.current?.(false)
    setSearchTerm('')
    if (!sentTermRef.current) {
      return
    }
    sentTermRef.current = ''
    onSearchRef.current?.('')
  }, [])

  // With a server-side onSearch the parent already returns narrowed options;
  // filtering again here would hide rows the server deliberately included.
  const visibleOptions = useMemo(() => {
    if (onSearch || !searchTerm) {
      return options
    }
    const term = searchTerm.toLowerCase()
    return options.filter((option) => option.name.toLowerCase().includes(term))
  }, [options, onSearch, searchTerm])

  const selectedIds = useMemo(
    () => (props.multiple ? props.value : props.value ? [props.value] : []),
    [props.multiple, props.value],
  )

  // The selected rows, remembered from when they were last in `options`.
  //
  // `options` is narrowed by search and the trigger label is looked up in it,
  // so picking a row that only a search surfaced and then clearing the search
  // left the selection unnameable: the assignee picker fell back to "Someone
  // else" and the prompt picker to "A prompt outside this list", while the
  // value underneath was correct all along. Only reachable past the first page
  // of results, which is why it survived local testing — caught in review.
  //
  // A ref rather than state: remembering a name must never itself cause a
  // render. Only selected ids are kept, so this cannot grow with paging.
  const rememberedSelection = useRef(new Map<string, PickerOption>())

  const optionById = useMemo(() => {
    const byId = new Map(options.map((option) => [option.id, option]))
    selectedIds.forEach((id) => {
      const live = byId.get(id)
      if (live) {
        // Still listed — refresh the memory, so a renamed row stays current.
        rememberedSelection.current.set(id, live)
        return
      }
      const remembered = rememberedSelection.current.get(id)
      if (remembered) {
        byId.set(id, remembered)
      }
    })
    return byId
  }, [options, selectedIds])

  const toggle = (id: string): void => {
    // Remember the row here, while it is certainly in front of us, rather than
    // letting the lookup below notice it later. Selecting from a searched list
    // also closes the dropdown, which clears the search: React batches both, so
    // the next render can have the new value and a list that no longer contains
    // it, with no render in between where it was both selected and listed.
    const picked = optionById.get(id)
    if (picked) {
      rememberedSelection.current.set(id, picked)
    }

    if (props.multiple) {
      props.onChange(
        props.value.includes(id)
          ? props.value.filter((existingId) => existingId !== id)
          : [...props.value, id],
      )
      return
    }
    props.onChange(id)
  }

  const clear = (): void => {
    if (props.multiple) {
      props.onChange([])
      return
    }
    props.onChange(CLEAR_ID)
  }

  const handleLoadMore = (event: MouseEvent): void => {
    // The popover closes on any click inside the list when it is single-select
    // (BasicPopoverWithSearch treats one as a selection), so asking for the next
    // page would shut the dropdown on the rows it just fetched.
    event.stopPropagation()
    if (!hasMore || !onLoadMore || isLoading || isLoadingMore) {
      return
    }
    onLoadMore()
  }

  const dimmedPlaceholder = placeholder ? (
    <Typography component="span" sx={{color: 'text.disabled', fontSize: 'inherit'}}>
      {placeholder}
    </Typography>
  ) : (
    ''
  )

  const singleTitle = (): ReactNode => {
    const selected = selectedIds[0] ? optionById.get(selectedIds[0]) : undefined
    if (!selected) {
      return dimmedPlaceholder
    }
    return renderOption ? renderOption(selected) : selected.name
  }

  const multiTitle = (): ReactNode => {
    if (selectedIds.length === 0) {
      return placeholder ? dimmedPlaceholder : t('add_item', {objectName: label})
    }
    if (selectedLabel) {
      return selectedLabel
    }
    return (
      <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center'}}>
        {selectedIds.map((id) => {
          const option = optionById.get(id)
          if (!option) {
            return null
          }
          // A caller-drawn entry is its own thing (usually already a chip), so
          // it gets no delete affordance grafted on.
          return renderOption ? (
            <Box key={id} sx={{display: 'flex', alignItems: 'center'}}>
              {renderOption(option)}
            </Box>
          ) : (
            <Chip
              key={id}
              label={option.name}
              size="small"
              variant="outlined"
              onDelete={() => toggle(id)}
              onMouseDown={(event) => event.stopPropagation()}
            />
          )
        })}
      </Box>
    )
  }

  const rows: ReactElement[] = []
  const isCheckable = Boolean(props.multiple) && checkableOptions

  if (props.multiple) {
    if (selectedIds.length > 0) {
      rows.push(
        <MenuItem
          key="__clear__"
          onClick={clear}
          sx={{fontSize: '0.85rem'}}
          data-cy={dataCy ? `${dataCy}-option` : undefined}
        >
          {clearLabel ?? t('clear_selection')}
        </MenuItem>,
      )
    }
  } else if (clearable) {
    // The clear row is the only way to get back to "nothing selected", so it is
    // always offered — even when the search term hides every other option.
    rows.push(
      <MenuItem
        key="__clear__"
        onClick={clear}
        selected={selectedIds.length === 0}
        data-cy={dataCy ? `${dataCy}-option` : undefined}
      >
        {clearLabel ?? t('none')}
      </MenuItem>,
    )
  }

  if (isError) {
    rows.push(
      <MenuItem key="__error__" disabled sx={{'&.Mui-disabled': {opacity: 1}}}>
        <Typography variant="body2" color="error">
          {t('options_load_failed')}
        </Typography>
      </MenuItem>,
    )
    if (onRetry) {
      rows.push(
        <MenuItem
          key="__retry__"
          disabled={isLoading}
          onClick={(event) => {
            // Same as load more: a click in a single-select list would close it.
            event.stopPropagation()
            onRetry()
          }}
          sx={{justifyContent: 'center'}}
        >
          <Typography variant="body2">{isLoading ? t('loading') : t('retry')}</Typography>
        </MenuItem>,
      )
    }
  } else if (visibleOptions.length === 0) {
    rows.push(
      <MenuItem key="__empty__" disabled>
        {searchPrompt && !searchTerm.trim() ? searchPrompt : t('no_data')}
      </MenuItem>,
    )
  } else {
    visibleOptions.forEach((option) => {
      const isDisabled = disabledIds.includes(option.id)
      const isSelected = selectedIds.includes(option.id)
      const row = (
        <MenuItem
          key={option.id}
          value={option.id}
          disabled={isDisabled}
          selected={isSelected}
          role={isCheckable ? 'menuitemcheckbox' : undefined}
          aria-checked={isCheckable ? isSelected : undefined}
          onClick={() => !isDisabled && toggle(option.id)}
          data-cy={dataCy ? `${dataCy}-option` : undefined}
          sx={{
            // Long names (e.g. program labels) must stay readable — wrap onto
            // multiple lines instead of clipping at the popover's right edge.
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            '&.Mui-selected': {
              backgroundColor: 'action.selected',
              '&:hover': {backgroundColor: 'action.hover'},
            },
            // The auto-focused first item otherwise renders a focus ring that
            // reads as a different style.
            '&.Mui-focusVisible': {backgroundColor: 'action.selected', outline: 'none'},
            '&.Mui-disabled': {opacity: 0.5, pointerEvents: 'auto'},
          }}
        >
          {props.multiple ? (
            <Box
              sx={{
                flexShrink: 0,
                width: 16,
                height: 16,
                borderRadius: 0.75,
                border: 1,
                borderColor: isSelected ? 'primary.main' : 'divider',
                backgroundColor: isSelected ? 'primary.main' : 'transparent',
                mr: 1,
              }}
            />
          ) : null}
          <Box sx={{minWidth: 0}}>
            {renderOption ? (
              renderOption(option)
            ) : (
              <Typography variant="body2" component="span">
                {option.name}
              </Typography>
            )}
            {option.description ? (
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {option.description}
              </Typography>
            ) : null}
          </Box>
        </MenuItem>
      )

      rows.push(
        isDisabled && disabledTooltip ? (
          <Tooltip key={option.id} title={disabledTooltip} placement="right">
            <span>{row}</span>
          </Tooltip>
        ) : (
          row
        ),
      )
    })
  }

  if (hasMore) {
    rows.push(
      <MenuItem
        key="__more__"
        disabled={isLoadingMore || isLoading || !onLoadMore}
        onClick={handleLoadMore}
        sx={{justifyContent: 'center'}}
      >
        <Typography variant="body2">
          {isLoadingMore || isLoading ? t('loading') : t('load_more')}
        </Typography>
      </MenuItem>,
    )
  }

  return (
    <Box
      data-testid={dataTestId}
      data-cy={dataCy}
      sx={{width: '100%', minWidth, maxWidth, display: 'flex', flexDirection: 'column'}}
    >
      {displayLabel && <BasicLabel label={label} />}
      <BasicPopoverWithSearch
        title={props.multiple ? multiTitle() : singleTitle()}
        disableButton={disabled}
        tooltip={
          tooltip ?? (parentName ? t('filter_by_for', {objectName: label, parentName}) : label)
        }
        header={parentName ? t('filter_for', {parentName}) : undefined}
        onSearchItemChange={handleSearchChange}
        onClose={handleClose}
        onOpen={handleOpen}
        isLoading={isLoading}
        maxWidth={popoverMaxWidth ?? maxWidth}
        leftIcon={icon}
        topRightBatchIcon={parentName ? <LinkIcon /> : undefined}
        size={size}
        minWidth={minWidth}
        multiSelect={props.multiple}
        allowTitleWrap={Boolean(props.multiple) && selectedIds.length > 0 && !selectedLabel}
        hideSearch={hideSearch}
        headerContent={headerContent}
        buttonDataCy={dataCy ? `${dataCy}-button` : undefined}
        buttonAriaDescribedBy={ariaDescribedBy}
        // Popovers are portalled to the body, so several filters' dropdowns can
        // coexist in the DOM with no reliable ordering. A per-filter hook is the
        // only unambiguous way to address one of them.
        menuDataCy={dataCy ? `${dataCy}-menu` : undefined}
      >
        <Stack sx={{width: '100%'}}>{rows}</Stack>
      </BasicPopoverWithSearch>
    </Box>
  )
}

export default Picker
